import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Badge, Nav } from 'react-bootstrap';
import Icon from './Icon.jsx';
import { Users } from 'lucide-react';

// WeeklyMatchups displays the full season's matchups and results.
// Input contract:
// - props.weeks: number of weeks in season
// - props.matchupsByWeek: { [weekNumber]: Array<{ homeTeamId, awayTeamId, homeName, awayName, homePoints, awayPoints, winnerTeamId }> }
// - props.teamsById: optional map of teamId -> team for name fallback
// - props.weekDateRanges: optional map of weekNumber -> { startLabel, endLabel }
const WeeklyMatchups = ({ weeks = 10, matchupsByWeek = {}, teamsById = {}, weekDateRanges = {} }) => {
  const weekNumbers = useMemo(
    () => Array.from({ length: weeks }, (_, index) => index + 1),
    [weeks]
  );
  const [selectedWeek, setSelectedWeek] = useState(weekNumbers[0] || 1);

  useEffect(() => {
    if (!weekNumbers.includes(selectedWeek)) {
      setSelectedWeek(weekNumbers[0] || 1);
    }
  }, [selectedWeek, weekNumbers]);

  const selectedWeekMatchups = matchupsByWeek[selectedWeek] || [];
  const selectedWeekDateRange = weekDateRanges[selectedWeek];
  const selectedWeekLabel = selectedWeekDateRange
    ? `Week ${selectedWeek} ${selectedWeekDateRange.startLabel}-${selectedWeekDateRange.endLabel}`
    : `Week ${selectedWeek}`;

  const renderRow = (m, idx) => {
    const home = m.homeName || teamsById[m.homeTeamId]?.name || 'TBD';
    const away = m.awayName || teamsById[m.awayTeamId]?.name || 'TBD';
    const hasResult = typeof m.homePoints === 'number' && typeof m.awayPoints === 'number';
    const winner = hasResult
      ? (m.homePoints === m.awayPoints ? null : (m.homePoints > m.awayPoints ? m.homeTeamId : m.awayTeamId))
      : null;
    return (
      <tr key={idx}>
        <td><strong>{home}</strong></td>
        <td className="text-center">vs</td>
        <td><strong>{away}</strong></td>
        <td className="text-end">
          {hasResult ? (
            <span>
              {m.homePoints?.toFixed ? m.homePoints.toFixed(1) : m.homePoints} - {m.awayPoints?.toFixed ? m.awayPoints.toFixed(1) : m.awayPoints}
              {' '}
              {winner ? (
                <Badge bg={m.isFinal ? 'success' : 'warning'} className="ms-2">
                  {m.isFinal ? 'Winner' : 'Leader'}: {winner === m.homeTeamId ? home : away}
                </Badge>
              ) : (
                <Badge bg="secondary" className="ms-2">Tie</Badge>
              )}
            </span>
          ) : (
            <Badge bg="secondary">Scheduled</Badge>
          )}
        </td>
      </tr>
    );
  };

  return (
    <Card className="modern-card mb-4">
      <div className="modern-card-header">
        <h3 className="modern-card-title"><Icon icon={Users} size={18} className="me-2 align-middle" /> Weekly Matchups</h3>
        <p className="modern-card-subtitle">All matchups for the season; past weeks show results</p>
      </div>
      <Card.Body>
        <Nav variant="tabs" className="mb-3 flex-nowrap overflow-auto" activeKey={String(selectedWeek)} onSelect={(eventKey) => setSelectedWeek(Number(eventKey) || 1)}>
          {weekNumbers.map((weekNum) => {
            const dateRange = weekDateRanges[weekNum];
            const tabLabel = dateRange
              ? `Week ${weekNum} ${dateRange.startLabel}-${dateRange.endLabel}`
              : `Week ${weekNum}`;

            return (
              <Nav.Item key={weekNum}>
                <Nav.Link eventKey={String(weekNum)}>{tabLabel}</Nav.Link>
              </Nav.Item>
            );
          })}
        </Nav>
        <div className="mb-3">
          <h6 className="text-primary">{selectedWeekLabel}</h6>
          <div className="table-responsive">
            <Table size="sm" hover>
              <tbody>
                {selectedWeekMatchups.map(renderRow)}
                {selectedWeekMatchups.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-secondary">No matchups scheduled</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default WeeklyMatchups;